/**
 * UserDetailPopup - Detailed user information popup for graph nodes
 *
 * Shows when a User node is clicked in the knowledge graph:
 * - User avatar with initials
 * - Tabs: Overview, Access, Activity
 * - AI Insights & Risk section with risk gauge (CLICKABLE for detailed view)
 * - Risk breakdown (High, Medium, Low)
 * - Insight cards (Dormant Account, Privilege Creep, etc.)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Card,
  CardContent,
  Avatar,
  Stack,
  Chip,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronRight as ChevronRightIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import apiService from '../../../services/apiService';

// Get initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.split(' ').filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Get risk color based on score
const getRiskColor = (score) => {
  if (score >= 80) return '#d27373'; // Red - Critical
  if (score >= 60) return '#d4925e'; // Orange - High
  if (score >= 40) return '#eab308'; // Yellow - Medium
  if (score >= 20) return '#5da87a'; // Green - Low
  return '#6b7280'; // Gray - Minimal
};

// Get risk level text
const getRiskLevel = (score) => {
  if (score >= 80) return 'Critical';
  if (score >= 60) return 'High';
  if (score >= 40) return 'Medium';
  if (score >= 20) return 'Low';
  return 'Minimal';
};

// Circular progress component for risk score
const RiskGauge = ({ score = 0, size = 80 }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const color = getRiskColor(score);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, display: 'inline-flex' }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(51, 65, 85, 0.5)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6" fontWeight={700} sx={{ fontSize: size * 0.25 }}>
          {score}
        </Typography>
      </Box>
    </Box>
  );
};

// Insight card component
const InsightCard = ({ title, description, severity, progress }) => {
  const getSeverityColor = () => {
    switch (severity) {
      case 'critical': return '#d27373';
      case 'high': return '#d4925e';
      case 'medium': return '#eab308';
      case 'low': return '#5da87a';
      default: return '#6b7280';
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1,
        bgcolor: 'rgba(255,255,255,0.02)',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
        {progress !== undefined && (
          <LinearProgress
            variant="determinate"
            value={Math.min(progress, 100)}
            sx={{
              mt: 1,
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.05)',
              '& .MuiLinearProgress-bar': {
                bgcolor: getSeverityColor(),
                borderRadius: 2,
              },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
};

// Detailed Risk Insights Modal Component
const RiskInsightsModal = ({
  insights,
  loading,
  onClose,
  userName,
  onFilterChange,
  selectedFilter
}) => {
  const getSeverityColor = (level) => {
    switch (level) {
      case 'high': return '#d27373';
      case 'medium': return '#f59e0b';
      case 'low': return '#5da87a';
      default: return '#6b7280';
    }
  };

  const filteredRisks = selectedFilter
    ? insights?.risks?.filter(r => r.level === selectedFilter) || []
    : insights?.risks || [];

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'background.paper',
          backgroundImage: 'none',
          maxHeight: '85vh',
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">AI Insights & Risk Analysis</Typography>
            <Typography variant="body2" color="text.secondary">
              Comprehensive security and access insights for {userName}
            </Typography>
          </Box>
          <Stack direction="row" spacing={2} alignItems="center">
            <RiskGauge score={insights?.riskScore || 0} size={60} />
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography color="text.secondary">Loading risk insights...</Typography>
          </Box>
        ) : (
          <>
            {/* Stats Row */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              {[
                { key: 'high', label: 'High Risk', value: insights?.highRisk || 0, color: '#d27373' },
                { key: 'medium', label: 'Medium Risk', value: insights?.mediumRisk || 0, color: '#f59e0b' },
                { key: 'low', label: 'Low Risk', value: insights?.lowRisk || 0, color: '#5da87a' },
                { key: null, label: 'Total Insights', value: insights?.totalInsights || 0, color: 'primary.main' },
              ].map((stat) => (
                <Card
                  key={stat.label}
                  variant="outlined"
                  onClick={() => onFilterChange(stat.key === selectedFilter ? null : stat.key)}
                  sx={{
                    flex: 1,
                    cursor: 'pointer',
                    borderColor: selectedFilter === stat.key ? stat.color : 'divider',
                    bgcolor: selectedFilter === stat.key ? 'action.selected' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: stat.color },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h5" fontWeight={700} sx={{ color: stat.color }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            {/* Filter indicator */}
            {selectedFilter && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Showing {selectedFilter} risk insights only. Click "Total Insights" to show all.
              </Typography>
            )}

            {/* Risk Cards Grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                gap: 2,
              }}
            >
              {filteredRisks.length > 0 ? (
                filteredRisks.map((risk, index) => (
                  <Card key={index} variant="outlined" sx={{ borderColor: 'divider' }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {risk.label}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        {risk.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Impact Level
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={risk.level === 'high' ? 100 : risk.level === 'medium' ? 66 : 33}
                        sx={{
                          mt: 0.5,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.05)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: getSeverityColor(risk.level),
                            borderRadius: 3,
                          },
                        }}
                      />
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary" sx={{ mb: 1 }}>
                    No {selectedFilter} risk insights found
                  </Typography>
                  <Button variant="text" onClick={() => onFilterChange(null)}>
                    Show All Insights
                  </Button>
                </Box>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Typography variant="caption" color="text.secondary">
          Last updated: {insights?.lastUpdated ? new Date(insights.lastUpdated).toLocaleDateString() : 'N/A'}
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const UserDetailPopup = ({
  user,
  onClose,
  onExplore,
  graphragService,
  tenantId = 'trustx-demo',
  reviewMode = false,
  reviewDecision = null,
  onReviewDecision,
}) => {
  const [decisionComment, setDecisionComment] = useState('');
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [insights, setInsights] = useState(null);

  // Detailed insights modal state
  const [showDetailedInsights, setShowDetailedInsights] = useState(false);
  const [detailedInsights, setDetailedInsights] = useState(null);
  const [detailedLoading, setDetailedLoading] = useState(false);
  const [insightFilter, setInsightFilter] = useState(null);

  // Extract user email from node data
  const userEmail = user?.email || user?.id || user?.properties?.email;
  const userName = user?.displayName || user?.name || user?.label || userEmail?.split('@')[0];

  console.log('UserDetailPopup - user node data:', {
    email: userEmail,
    name: userName,
    nodeType: user?.type,
    rawUser: user
  });

  // Fetch user details and insights using getUserProfile360
  const fetchUserData = useCallback(async () => {
    console.log('UserDetailPopup - fetchUserData called', { userEmail, hasService: !!graphragService });

    if (!userEmail) {
      console.warn('UserDetailPopup - No email found in user node');
      setInsights(extractInsightsFromNode(user));
      setLoading(false);
      return;
    }

    if (!graphragService) {
      console.warn('UserDetailPopup - No graphragService provided');
      setInsights(extractInsightsFromNode(user));
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      console.log('UserDetailPopup - Calling getUserProfile360 for:', userEmail);
      const profile = await graphragService.getUserProfile360(userEmail);

      console.log('UserDetailPopup - profile360 response:', profile);

      if (profile.success || profile.user) {
        const parsedInsights = {
          riskScore: profile.risks?.overallScore || 0,
          riskCounts: profile.risks?.riskCounts || { high: 0, medium: 0, low: 0 },
          dormant: profile.risks?.dormant ? {
            daysInactive: profile.risks.dormant.daysInactive,
            risk: profile.risks.dormant.riskLevel || 'medium',
            description: `Account inactive for ${profile.risks.dormant.daysInactive} days`
          } : null,
          privilegeCreep: profile.risks?.privilegeCreep ? {
            score: profile.risks.privilegeCreep.score,
            risk: profile.risks.privilegeCreep.classification || 'medium',
            description: profile.risks.privilegeCreep.reasoning || 'Accumulated permissions exceed role requirements',
            privilegedGroups: profile.risks.privilegeCreep.privilegedGroups || [],
            privilegedGroupCount: profile.risks.privilegeCreep.privilegedGroupCount || 0
          } : null,
          excessiveAccess: profile.risks?.excessiveAccess ? {
            score: profile.risks.excessiveAccess.score,
            zScore: profile.risks.excessiveAccess.zScore,
            risk: profile.risks.excessiveAccess.riskLevel || 'medium',
            description: profile.risks.excessiveAccess.description || 'Access levels exceed peer average'
          } : null,
          lastSeen: profile.lastSeen || profile.user?.lastSignIn,
          status: profile.status || profile.user?.status || 'Active',
          jobTitle: profile.user?.jobTitle || profile.user?.job_title || user?.jobTitle || user?.job_title || null,
          department: profile.user?.department || user?.department || null,
          groups: profile.groups || [],
          applications: profile.applications || [],
          manager: profile.manager,
          directReports: profile.directReports || []
        };
        setInsights(parsedInsights);
      } else {
        console.log('Profile fetch failed, using node data');
        setInsights(extractInsightsFromNode(user));
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      setInsights(extractInsightsFromNode(user));
    } finally {
      setLoading(false);
    }
  }, [userEmail, graphragService, user]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Extract insights from node data (fallback)
  const extractInsightsFromNode = (node) => {
    const props = { ...node, ...(node.properties || {}) };

    const insights = {
      riskScore: node.riskScore || node.risk_score || 0,
      riskCounts: { high: 0, medium: 0, low: 0 },
      dormant: null,
      privilegeCreep: null,
      excessiveAccess: null,
      lastSeen: node.lastSignIn || node.lastSeen || null,
      status: node.status || 'Active',
      jobTitle: props.jobTitle || props.job_title || props.title || null,
      department: props.department || props.department_name || null,
    };

    if (props.privilegeCreepScore !== undefined) {
      insights.privilegeCreep = {
        score: props.privilegeCreepScore,
        risk: props.privilegeCreepRisk || getRiskLevel(props.privilegeCreepScore),
        description: 'More roles than needed'
      };
    }

    if (props.daysInactive !== undefined) {
      insights.dormant = {
        daysInactive: props.daysInactive,
        risk: props.daysInactive > 90 ? 'high' : props.daysInactive > 60 ? 'medium' : 'low',
        description: `Account inactivity > ${props.daysInactive} days`
      };
    }

    if (props.zScore !== undefined) {
      insights.excessiveAccess = {
        score: Math.round(props.zScore * 20),
        zScore: props.zScore,
        risk: props.zScore > 3 ? 'high' : props.zScore > 2 ? 'medium' : 'low',
        description: 'Access levels exceed peer average'
      };
    }

    return insights;
  };

  // Handle opening detailed insights modal
  const handleOpenDetailedInsights = useCallback(async () => {
    setShowDetailedInsights(true);
    setDetailedLoading(true);
    setInsightFilter(null);

    try {
      if (graphragService && userEmail) {
        const result = await graphragService.getUserRiskInsights(userEmail, apiService);
        if (result.success && result.data) {
          setDetailedInsights(result.data);
        } else {
          setDetailedInsights(result.data || null);
        }
      }
    } catch (err) {
      console.error('Failed to fetch detailed insights:', err);
    } finally {
      setDetailedLoading(false);
    }
  }, [graphragService, userEmail]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  if (!user) return null;

  const currentInsights = insights || extractInsightsFromNode(user);

  const InfoRow = ({ label, value, statusColor }) => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.75 }}
    >
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={500}
        sx={statusColor ? { color: statusColor } : {}}
      >
        {value}
      </Typography>
    </Stack>
  );

  const statusColorMap = {
    active: '#5da87a',
    inactive: '#d27373',
    disabled: '#6b7280',
  };

  return (
    <>
      <Drawer
        anchor="right"
        open
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 360,
            bgcolor: 'background.default',
            backgroundImage: 'none',
            borderLeft: 1,
            borderColor: 'divider',
          },
        }}
        ModalProps={{ keepMounted: false }}
      >
        {/* Close button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Header with avatar */}
        <Box sx={{ textAlign: 'center', px: 3, pb: 2 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              mx: 'auto',
              mb: 1.5,
              bgcolor: '#8b5cf6',
              fontSize: '1.25rem',
              fontWeight: 700,
            }}
          >
            {getInitials(userName)}
          </Avatar>
          <Typography variant="h6" fontWeight={600}>{userName}</Typography>
          <Typography variant="body2" color="text.secondary">{userEmail}</Typography>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 36,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 36,
              fontSize: '0.8rem',
              textTransform: 'none',
            },
          }}
        >
          <Tab label="Overview" />
          <Tab label="Access" />
          <Tab label="Activity" />
        </Tabs>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography variant="body2" color="text.secondary">Loading insights...</Typography>
            </Box>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 0 && (
                <Box>
                  {/* Basic info */}
                  {currentInsights.jobTitle && (
                    <InfoRow label="Role" value={currentInsights.jobTitle} />
                  )}
                  {currentInsights.department && (
                    <InfoRow label="Department" value={currentInsights.department} />
                  )}
                  <InfoRow label="Last Seen" value={formatDate(currentInsights.lastSeen)} />
                  <InfoRow
                    label="Status"
                    value={currentInsights.status}
                    statusColor={statusColorMap[currentInsights.status?.toLowerCase()]}
                  />

                  <Divider sx={{ my: 1.5 }} />

                  {/* AI Insights & Risk Section - CLICKABLE */}
                  <Card
                    variant="outlined"
                    onClick={handleOpenDetailedInsights}
                    sx={{
                      cursor: 'pointer',
                      borderColor: 'divider',
                      bgcolor: 'rgba(255,255,255,0.02)',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                      transition: 'all 0.2s',
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                        <Typography variant="subtitle2">AI Insights & Risk</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <RiskGauge score={currentInsights.riskScore} size={50} />
                          <ChevronRightIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                        </Stack>
                      </Stack>

                      {/* Risk counts */}
                      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                        {[
                          { label: 'High', value: currentInsights.riskCounts.high, color: '#d27373' },
                          { label: 'Medium', value: currentInsights.riskCounts.medium, color: '#eab308' },
                          { label: 'Low', value: currentInsights.riskCounts.low, color: '#5da87a' },
                        ].map((item) => (
                          <Box key={item.label} sx={{ textAlign: 'center', flex: 1 }}>
                            <Typography variant="h6" fontWeight={700} sx={{ color: item.color }}>
                              {item.value}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {item.label} Risk
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      {/* Insight cards (preview) */}
                      {currentInsights.dormant && (
                        <InsightCard
                          title="Dormant Account"
                          description={currentInsights.dormant.description}
                          severity={currentInsights.dormant.risk}
                          progress={Math.min((currentInsights.dormant.daysInactive / 90) * 100, 100)}
                        />
                      )}

                      {currentInsights.privilegeCreep && (
                        <InsightCard
                          title={`Privilege Creep (${currentInsights.privilegeCreep.privilegedGroupCount || 0} privileged groups)`}
                          description={currentInsights.privilegeCreep.description}
                          severity={currentInsights.privilegeCreep.risk}
                          progress={currentInsights.privilegeCreep.score}
                        />
                      )}

                      {!currentInsights.dormant && !currentInsights.privilegeCreep && currentInsights.excessiveAccess && (
                        <InsightCard
                          title="Excessive Access"
                          description={currentInsights.excessiveAccess.description}
                          severity={currentInsights.excessiveAccess.risk}
                          progress={currentInsights.excessiveAccess.score}
                        />
                      )}

                      {!currentInsights.dormant && !currentInsights.privilegeCreep && !currentInsights.excessiveAccess && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2, justifyContent: 'center' }}>
                          <ShieldIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
                          <Typography variant="body2" color="text.secondary">
                            No risk indicators detected
                          </Typography>
                        </Box>
                      )}

                      {/* View All Button */}
                      <Typography
                        variant="caption"
                        color="primary"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mt: 1,
                          gap: 0.5,
                        }}
                      >
                        View All 9 Insights <ChevronRightIcon sx={{ fontSize: 16 }} />
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Access Tab */}
              {activeTab === 1 && (
                <Box>
                  {/* Groups */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Groups ({currentInsights.groups?.length || 0})
                  </Typography>
                  {currentInsights.groups && currentInsights.groups.length > 0 ? (
                    <Stack spacing={0.5} sx={{ mb: 3 }}>
                      {currentInsights.groups.map((group, idx) => {
                        const isPrivileged = group.isAdmin ||
                          (currentInsights.privilegeCreep?.privilegedGroups || []).includes(group.name);
                        return (
                          <Card key={idx} variant="outlined" sx={{ borderColor: 'divider' }}>
                            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ fontSize: '1rem' }}>{'👥'}</Typography>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={500} noWrap>
                                    {group.name || group.displayName}
                                  </Typography>
                                  {group.description && (
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                      {group.description}
                                    </Typography>
                                  )}
                                </Box>
                                {isPrivileged && (
                                  <Chip label="Privileged" size="small" color="warning"
                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                  />
                                )}
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      No group memberships found
                    </Typography>
                  )}

                  {/* Applications */}
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Applications ({currentInsights.applications?.length || 0})
                  </Typography>
                  {currentInsights.applications && currentInsights.applications.length > 0 ? (
                    <Stack spacing={0.5}>
                      {currentInsights.applications.map((app, idx) => (
                        <Card key={idx} variant="outlined" sx={{ borderColor: 'divider' }}>
                          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography sx={{ fontSize: '1rem' }}>{'📱'}</Typography>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={500} noWrap>
                                  {app.name || app.displayName}
                                </Typography>
                                {app.description && (
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {app.description}
                                  </Typography>
                                )}
                              </Box>
                            </Stack>
                          </CardContent>
                        </Card>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No application assignments found
                    </Typography>
                  )}
                </Box>
              )}

              {/* Activity Tab */}
              {activeTab === 2 && (
                <Box>
                  <InfoRow label="Last Sign-in" value={formatDate(currentInsights.lastSeen)} />
                  <InfoRow
                    label="Account Status"
                    value={currentInsights.status}
                    statusColor={statusColorMap[currentInsights.status?.toLowerCase()]}
                  />

                  {/* Manager */}
                  {currentInsights.manager && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Reports To</Typography>
                      <Card variant="outlined" sx={{ borderColor: 'divider' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{ width: 36, height: 36, bgcolor: '#3b82f6', fontSize: '0.8rem' }}
                            >
                              {getInitials(currentInsights.manager.name || currentInsights.manager.displayName)}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={500}>
                                {currentInsights.manager.name || currentInsights.manager.displayName}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {currentInsights.manager.jobTitle}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block">
                                {currentInsights.manager.email}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Box>
                  )}

                  {/* Direct Reports */}
                  {currentInsights.directReports && currentInsights.directReports.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Direct Reports ({currentInsights.directReports.length})
                      </Typography>
                      <Stack spacing={0.5}>
                        {currentInsights.directReports.slice(0, 5).map((report, idx) => (
                          <Card key={idx} variant="outlined" sx={{ borderColor: 'divider' }}>
                            <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Avatar
                                  sx={{ width: 28, height: 28, bgcolor: '#10b981', fontSize: '0.65rem' }}
                                >
                                  {getInitials(report.name || report.displayName)}
                                </Avatar>
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {report.name || report.displayName}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {report.email}
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        ))}
                        {currentInsights.directReports.length > 5 && (
                          <Typography variant="caption" color="text.secondary">
                            +{currentInsights.directReports.length - 5} more
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  )}

                  {!currentInsights.manager && (!currentInsights.directReports || currentInsights.directReports.length === 0) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      No organizational relationships found
                    </Typography>
                  )}
                </Box>
              )}

              {/* Review decision buttons (visible in review mode) */}
              {reviewMode && onReviewDecision && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  {reviewDecision ? (
                    <Chip
                      label={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <span>
                            {reviewDecision.decision === 'approve' ? '✓' :
                             reviewDecision.decision === 'flag' ? '⚑' : '✕'}
                          </span>
                          <span>
                            {reviewDecision.decision === 'approve' ? 'Approved' :
                             reviewDecision.decision === 'flag' ? 'Flagged' : 'Revoked'}
                          </span>
                        </Stack>
                      }
                      color={
                        reviewDecision.decision === 'approve' ? 'success' :
                        reviewDecision.decision === 'flag' ? 'warning' : 'error'
                      }
                      sx={{ mb: reviewDecision.comment ? 1 : 0 }}
                    />
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Add a comment (optional)..."
                        value={decisionComment}
                        onChange={(e) => setDecisionComment(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          disabled={decisionLoading}
                          onClick={async () => {
                            setDecisionLoading(true);
                            await onReviewDecision(userEmail, 'approve', decisionComment || null);
                            setDecisionComment('');
                            setDecisionLoading(false);
                          }}
                          sx={{ flex: 1 }}
                        >
                          Approve
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="warning"
                          disabled={decisionLoading}
                          onClick={async () => {
                            setDecisionLoading(true);
                            await onReviewDecision(userEmail, 'flag', decisionComment || null);
                            setDecisionComment('');
                            setDecisionLoading(false);
                          }}
                          sx={{ flex: 1 }}
                        >
                          Flag
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          disabled={decisionLoading}
                          onClick={async () => {
                            setDecisionLoading(true);
                            await onReviewDecision(userEmail, 'revoke', decisionComment || null);
                            setDecisionComment('');
                            setDecisionLoading(false);
                          }}
                          sx={{ flex: 1 }}
                        >
                          Revoke
                        </Button>
                      </Stack>
                    </>
                  )}
                </Box>
              )}

              {/* Explore button */}
              {onExplore && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => onExplore(user)}
                    sx={{ textTransform: 'none' }}
                  >
                    Explore User Graph
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      </Drawer>

      {/* Detailed Risk Insights Modal - rendered outside drawer for correct z-index stacking */}
      {showDetailedInsights && (
        <RiskInsightsModal
          insights={detailedInsights}
          loading={detailedLoading}
          onClose={() => {
            setShowDetailedInsights(false);
            setInsightFilter(null);
          }}
          userName={userName}
          onFilterChange={setInsightFilter}
          selectedFilter={insightFilter}
        />
      )}
    </>
  );
};

export default UserDetailPopup;
